import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Mail, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable "Notify client?" dialog.
 * Props:
 *   open, onClose
 *   client: { id, client_name, mobile, email }
 *   task: { id, title } (optional — used for default message + reason)
 *   reason: string
 *   defaultBody: string (optional override)
 */
export default function NotifyClientDialog({ open, onClose, client, task, reason = "task_completed", defaultBody }) {
  const [firm, setFirm] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) api.get("/firm").then(({ data }) => setFirm(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !client) return;
    const signature = firm?.signature || "Team Vijay";
    const firmName = firm?.name || "";
    const taskTitle = task?.title || "work assigned to us";

    let body;
    if (defaultBody) {
      body = defaultBody;
    } else if (firm?.notification_template) {
      body = firm.notification_template
        .replaceAll("{client_name}", client.client_name || "")
        .replaceAll("{task_title}", taskTitle)
        .replaceAll("{signature}", signature)
        .replaceAll("{firm_name}", firmName);
    } else {
      body = (
        `Dear ${client.client_name},\n\n` +
        `Your ${taskTitle} has been successfully completed.\n\n` +
        `If you have any questions or require a copy of the filed return or acknowledgment, please feel free to contact us.\n\n` +
        `Thank you for your continued trust in our services.\n` +
        `${signature}`
      );
    }
    setBody(body);
    setSubject(task?.title ? `Update: ${task.title}` : `Update from ${firmName || "us"}`);
  }, [open, client, task, firm, defaultBody]);

  async function send(channel) {
    if (!client) return;
    if (channel === "whatsapp" && !client.mobile) { toast.error("No mobile on file for this client"); return; }
    if (channel === "email" && !client.email) { toast.error("No email on file for this client"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send", {
        client_id: client.id,
        channel,
        subject,
        message: body,
        task_id: task?.id,
        reason,
      });
      if (channel === "whatsapp" && data.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank");
        toast.success("WhatsApp opened — hit Send in the chat");
      } else {
        toast.success(`Email sent to ${data.to || client.email}`);
      }
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="notify-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" />Notify client?</DialogTitle></DialogHeader>
        {client && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-md bg-slate-50 dark:bg-zinc-900 border border-border">
              <div className="w-9 h-9 rounded-full bg-blue-600 text-white grid place-items-center font-display font-bold text-sm">
                {client.client_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{client.client_name}</div>
                <div className="text-[10px] text-muted-foreground font-mono flex gap-3">
                  {client.mobile ? <span>📱 {client.mobile}</span> : <span className="text-red-600">no mobile</span>}
                  {client.email ? <span>✉️ {client.email}</span> : <span className="text-red-600">no email</span>}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Email Subject (for email only)</Label>
              <Input data-testid="notify-subject-input" value={subject} onChange={e => setSubject(e.target.value)} className="mt-1" />
            </div>

            <div>
              <Label className="text-xs">Message</Label>
              <Textarea data-testid="notify-message-input" value={body} onChange={e => setBody(e.target.value)} rows={7} className="mt-1 font-mono text-xs" />
              <div className="text-[10px] text-muted-foreground mt-1">Signature is set in Firm Settings and can be edited here per-send.</div>
            </div>

            {!firm?.smtp_password_configured && (
              <div className="flex items-start gap-2 text-[11px] p-2 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-200">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>SMTP not configured — email sending will fail. Set it up in <b>Firm Settings → Email delivery</b>.</div>
              </div>
            )}

            <DialogFooter className="gap-2 flex-wrap">
              <Button data-testid="notify-skip-btn" variant="outline" onClick={() => onClose(false)}>
                <X className="w-4 h-4 mr-1.5" /> Skip
              </Button>
              <Button data-testid="notify-email-btn" variant="outline" onClick={() => send("email")}
                      disabled={sending || !client.email} className="border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-900 dark:text-blue-300">
                <Mail className="w-4 h-4 mr-1.5" /> Send Email
              </Button>
              <Button data-testid="notify-whatsapp-btn" onClick={() => send("whatsapp")}
                      disabled={sending || !client.mobile} className="bg-emerald-600 hover:bg-emerald-700">
                <MessageCircle className="w-4 h-4 mr-1.5" /> Send WhatsApp
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
